import { UserRoleManager, UserRoles } from "core";
import React from "react";
import { debounce, isSmallDeviceWidth } from "component-library";
import { UserTypes } from "shared-types";

import { BasicInfo, FacetCategory, IAgencyList, Photo } from "models";
import { SocialMedia } from "models/Social";

import { IMainPhotoContent, MainPhoto } from "components/common/MainPhoto";

import { AgenciesSection, IAgenciesSectionContent } from "../AgenciesSection";
import {
  BasicInfoSection,
  IBasicInfoSectionContent,
} from "../BasicInfoSection";
import { RowGallery } from "../RowGallery";
import { ISocialMediaListContent, SocialMediaList } from "../SocialMediaList";

import "./GalleryPhotoSection.scss";

export interface IGalleryPhotoSectionContent {
  mainPhotoContent: IMainPhotoContent;
  basicInfoSectionContent: IBasicInfoSectionContent;
  agenciesSectionContent: IAgenciesSectionContent;
  socialMediaSectionContent: ISocialMediaListContent;
  noPhotoPlaceholder: string;
  hiddenPhotoText: string;
  editGalleryText: string;
  nextButtonText: string;
  prevButtonText: string;
  galleryPhotoSectionDescription: string;
  galleryRowDescription: string;
  galleryPhotoDescription: string;
}

export interface IGalleryPhotoSectionProps {
  basicInfoFacets: FacetCategory[];
  content: IGalleryPhotoSectionContent;
  mainPhotoError: boolean;
  isReadonly: boolean;
  socialMedia?: SocialMedia;
  mainPhoto?: Photo;
  galleryPhotos?: Photo[];
  basicInfo?: BasicInfo;
  agencies?: IAgencyList;
  openPhoto: (photo: Photo) => void;
  editGallery: () => void;
  editSocialMedia: () => void;
  editBasicInfo: () => void;
  editAgencies: () => void;
  openPerformerContactPopup: (
    event: React.MouseEvent<HTMLAnchorElement>
  ) => void;
  isSelfRepresented?: boolean;
  isLookingForRepresentation?: boolean;
  isToastVisible: boolean;
  closeToast: () => void;
  isFilmmakersProfile?: boolean;
  isSLProfileInFilmmakers?: boolean;
  userType: UserTypes;
}

interface IGalleryPhotoSectionState {
  isBasicInfoExpanded: boolean;
  isAgenciesExpanded: boolean;
  isMobile: boolean;
}

export class GalleryPhotoSection extends React.Component<
  IGalleryPhotoSectionProps,
  IGalleryPhotoSectionState
> {
  public readonly state: Readonly<IGalleryPhotoSectionState> = {
    isBasicInfoExpanded: false,
    isAgenciesExpanded: false,
    isMobile: false,
  };

  private basicInfoExpandableContainerRef = React.createRef<HTMLDivElement>();
  private agenciesExpandableContainerRef = React.createRef<HTMLDivElement>();
  private photoContainerRef = React.createRef<HTMLDivElement>();

  public render() {
    const {
      mainPhoto,
      mainPhotoError,
      openPhoto,
      content,
      basicInfo,
      basicInfoFacets,
      editBasicInfo,
      agencies,
      editAgencies,
      socialMedia,
      isReadonly,
      editSocialMedia,
      isSelfRepresented,
      isLookingForRepresentation,
      isToastVisible,
      isFilmmakersProfile,
      isSLProfileInFilmmakers,
      userType,
    } = this.props;

    const { isBasicInfoExpanded, isAgenciesExpanded, isMobile } = this.state;

    const basicInfoExpandableContainer = (
      <div
        className="c-gallery-photo-section__expandable-area"
        ref={this.basicInfoExpandableContainerRef}
      />
    );
    const agenciesExpandableContainer = (
      <div
        className="c-gallery-photo-section__expandable-area"
        ref={this.agenciesExpandableContainerRef}
      />
    );

    const openMainPhoto = () => mainPhoto && openPhoto(mainPhoto);

    return (
      <section
        className="c-gallery-photo-section"
        aria-label={content.galleryPhotoSectionDescription}
      >
        <div className="c-gallery-photo-section__info">
          <div className="c-gallery-photo-section__main-photo">
            <div ref={this.photoContainerRef}>
              <MainPhoto
                showError={mainPhotoError}
                photo={mainPhoto}
                content={content.mainPhotoContent}
                openPhoto={openMainPhoto}
              />
              <SocialMediaList
                content={content.socialMediaSectionContent}
                socialMedia={socialMedia}
                editSocialMedia={editSocialMedia}
                isEditAvailable={UserRoleManager.isInRole(UserRoles.spotlight)}
                isFilmmakersProfile={isFilmmakersProfile}
                isSLProfileInFilmmakers={isSLProfileInFilmmakers}
                profileId={basicInfo?.profileId}
                isReadonly={isReadonly}
                isAgent={UserRoleManager.isInRole(UserRoles.agent)}
                userType={userType}
              />
            </div>
          </div>
          <div className="c-gallery-photo-section__info-container">
            <div className="c-gallery-photo-section__info-container-section">
              <BasicInfoSection
                basicInfo={basicInfo}
                basicInfoFacets={basicInfoFacets}
                editBasicInfo={editBasicInfo}
                content={{
                  ...content.basicInfoSectionContent,
                  editLinkAltText: content.editGalleryText,
                }}
                outerContainer={this.photoContainerRef}
                expandableContainer={this.basicInfoExpandableContainerRef}
                isExpanded={isBasicInfoExpanded}
                isReadonly={isReadonly}
                toggleExpand={this.toggleBasicInfoExpand}
                isToastVisible={isToastVisible}
                closeToast={this.props.closeToast}
              />
              {isMobile && basicInfoExpandableContainer}
            </div>
            <div className="c-gallery-photo-section__info-container-section">
              <AgenciesSection
                agencies={agencies}
                editAgencies={editAgencies}
                content={{
                  ...content.agenciesSectionContent,
                  editLinkAltText: content.editGalleryText,
                }}
                expandableContainer={this.agenciesExpandableContainerRef}
                outerContainer={this.photoContainerRef}
                isExpanded={isAgenciesExpanded}
                isReadonly={isReadonly}
                toggleExpand={this.toggleAgenciesExpand}
                openPerformerContactPopup={this.props.openPerformerContactPopup}
                isSelfRepresented={isSelfRepresented}
                isLookingForRepresentation={isLookingForRepresentation}
                isFilmmakersProfile={isFilmmakersProfile}
              />
              {isMobile && agenciesExpandableContainer}
            </div>
          </div>
        </div>
        {!isMobile && (
          <React.Fragment>
            {basicInfoExpandableContainer}
            {agenciesExpandableContainer}
          </React.Fragment>
        )}
        {this.renderGallery()}
      </section>
    );
  }

  public componentDidMount() {
    window.addEventListener("resize", this.trackScreenSizeChanges, false);
    this.trackScreenSizeChanges();
  }

  public componentWillUnmount() {
    window.removeEventListener("resize", this.trackScreenSizeChanges, false);
  }

  private renderGallery = () => {
    const { openPhoto, editGallery, content, isReadonly } = this.props;

    let galleryPhotos = this.props.galleryPhotos;
    let isVisible = true;

    if (galleryPhotos && isReadonly) {
      galleryPhotos = galleryPhotos.filter((photo) => !photo.isHidden);

      if (galleryPhotos.length === 0) {
        isVisible = false;
      }
    }

    return (
      isVisible && (
        <section
          className="c-gallery-photo-section__gallery"
          aria-label={content.galleryRowDescription}
        >
          <RowGallery
            isEditHidden={isReadonly}
            photos={galleryPhotos}
            openPhoto={openPhoto}
            editGallery={editGallery}
            noPhotoText={content.noPhotoPlaceholder}
            hiddenPhotoText={content.hiddenPhotoText}
            editGalleryText={content.editGalleryText}
            nextButtonText={content.nextButtonText}
            prevButtonText={content.prevButtonText}
            galleryPhotoDescription={content.galleryPhotoDescription}
          />
        </section>
      )
    );
  };

  private toggleBasicInfoExpand = () => {
    const { isBasicInfoExpanded } = this.state;
    const stateUpdates = {
      ...this.state,
      isBasicInfoExpanded: !isBasicInfoExpanded,
    };

    if (!isSmallDeviceWidth()) {
      stateUpdates.isAgenciesExpanded = false;
    }

    this.setState(stateUpdates);
  };

  private toggleAgenciesExpand = () => {
    const { isAgenciesExpanded } = this.state;
    const stateUpdates = {
      ...this.state,
      isAgenciesExpanded: !isAgenciesExpanded,
    };

    if (!isSmallDeviceWidth()) {
      stateUpdates.isBasicInfoExpanded = false;
    }

    this.setState(stateUpdates);
  };

  private trackScreenSizeChanges = debounce(() => {
    const isScreenSizeSmall = isSmallDeviceWidth();
    const isScreenSizeChanged = this.state.isMobile !== isScreenSizeSmall;

    if (isScreenSizeChanged) {
      this.setState({
        isMobile: isScreenSizeSmall,
        isAgenciesExpanded: false,
        isBasicInfoExpanded: false,
      });
    }
  }, 300);
}
